import { BasicUser, FolderListRow, OrganizationRole } from '@st/sdk'
import { AssigneeBadge, AssigneeList, AssignIcon, SearchableOptionMenu } from '@st/theme'
import { isNotEmpty } from '@st/util/json-value'
import { clsx } from 'clsx'
import { useState } from 'react'
import {
  selOptimisticFolderAssigneeIds,
  STFolderAction,
  stFolderListModule
} from './st-folder-list-module'
import { formatAssigneeInitials } from './assignee-utils'
import { useSelect } from '~/store'

export function AssigneeCell({
  folder,
  organizationUsers,
  organizationRole,
  onAction
}: {
  folder: FolderListRow
  organizationRole: OrganizationRole
  organizationUsers: BasicUser[]
  onAction: (action: STFolderAction) => void
}) {
  const [isOpen, setIsOpen] = useState(false)

  const assigneeIds = useSelect('stFolderList', (state) =>
    selOptimisticFolderAssigneeIds(state, folder.id)
  )
  const assignees = assigneeIds
    .map((id) => organizationUsers.find((u) => u.id == id))
    .filter(isNotEmpty)

  const displayName = (user: BasicUser): string | undefined => user.name ?? user.email

  const handleToggleUser = (user: BasicUser, isAssignee: boolean) => {
    setIsOpen(false)
    if (isAssignee) {
      onAction({ type: 'unassignFolder', folder: folder, userId: user.id })
    } else {
      onAction({ type: 'assignFolder', folder: folder, userId: user.id })
    }
  }

  return (
    <AssigneeList
      className={clsx('h-full')}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      disabled={organizationRole == 'restricted'}
      ariaLabel="Edit assignees"
      menu={
        <SearchableOptionMenu
          title="Assignees"
          placeholder="Search assignees"
          emptyLabel="No matching assignees"
          items={organizationUsers.map((user) => {
            const isAssignee = assigneeIds.includes(user.id)

            return {
              id: user.id,
              label: displayName(user),
              searchText: `${user.name ?? ''} ${user.email ?? ''}`,
              selected: isAssignee,
              leading: (
                <AssigneeBadge color={isAssignee ? 'gray' : 'outline'}>
                  {formatAssigneeInitials(user)}
                </AssigneeBadge>
              )
            }
          })}
          onRequestClose={() => setIsOpen(false)}
          onSelect={(userId) => {
            const user = organizationUsers.find((candidate) => candidate.id == userId)
            if (!user) {
              return
            }

            handleToggleUser(user, assigneeIds.includes(user.id))
          }}
        />
      }
    >
      {assignees.map((user) => (
        <AssigneeBadge key={user.id} color="gray" tip={displayName(user)}>
          {formatAssigneeInitials(user)}
        </AssigneeBadge>
      ))}

      <AssignIcon
        className={clsx(
          'text-gray-500 hover:text-blue-500',
          // keep this visible if the menu is open
          !isOpen && 'invisible group-hover:visible'
        )}
      />
    </AssigneeList>
  )
}
